import React from 'react';
import CardSlot from '../parts/CardSlot';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { translateDay } from 'helpers/translateDays';
import { Clock } from 'react-feather';

const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function getCurrentDayKey() {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
}

export default function ProfileHours({ data }) {
  const currentDay = getCurrentDayKey();

  return (
    <CardSlot marginTop={'10px'}>
      <Header>
        <IconBox>
          <Clock size={18} color={colors.neutralLight} />
        </IconBox>
        <HeaderText>
          <Title>Horários de funcionamento</Title>
          <Subtitle>Dias e horários de operação</Subtitle>
        </HeaderText>
      </Header>

      <HoursList>
        {data?.dates &&
          dayOrder.map((day, i) => {
            const hours = data.dates[day];
            if (hours === undefined) return null;
            const isToday = day === currentDay;
            const isClosed = !hours || hours.length === 0;
            const is24h = hours && hours.some((h) => h['24h']);

            return (
              <HoursRow key={day} isToday={isToday} index={i}>
                <DayLeft>
                  <StatusDot isClosed={isClosed} isToday={isToday} />
                  <DayLabel isToday={isToday}>{translateDay(day)}</DayLabel>
                  {isToday && <TodayBadge>Hoje</TodayBadge>}
                </DayLeft>

                <HoursContent>
                  {isClosed && <ClosedChip>Fechado</ClosedChip>}

                  {!isClosed && !is24h &&
                    hours.map((hour, idx) => (
                      <TimeChip key={idx} isToday={isToday}>
                        {hour.start}{hour.end ? ` \u2014 ${hour.end}` : ''}
                      </TimeChip>
                    ))}

                  {is24h && <AllDayChip>24 horas</AllDayChip>}
                </HoursContent>
              </HoursRow>
            );
          })}
      </HoursList>
    </CardSlot>
  );
}

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(63, 107, 198, 0.25);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.h3`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  margin: 0;
`;

const HoursList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HoursRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 10px;
  transition: background 0.2s ease;
  animation: ${fadeSlideUp} 0.35s ease forwards;
  animation-delay: ${({ index }) => index * 0.045}s;
  opacity: 0;

  ${({ isToday }) => isToday && `
    background: rgba(79, 134, 247, 0.06);
    box-shadow: inset 3px 0 0 0 rgba(79, 134, 247, 0.7);
  `}
`;

const DayLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ isClosed }) => isClosed ? colors.nightLight : colors.success};
  box-shadow: ${({ isClosed, isToday }) =>
    !isClosed && isToday
      ? '0 0 0 3px rgba(43, 200, 102, 0.15)'
      : 'none'};
`;

const DayLabel = styled.span`
  font-size: ${fonts.sizes.default};
  font-weight: ${({ isToday }) => isToday ? fonts.weight.bold : fonts.weight.regular};
  color: ${({ isToday }) => isToday ? colors.nightDark : colors.night};
`;

const TodayBadge = styled.span`
  font-size: 9px;
  font-weight: ${fonts.weight.bold};
  color: ${colors.primaryLight};
  background: rgba(79, 134, 247, 0.1);
  padding: 2px 7px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HoursContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TimeChip = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${({ isToday }) => isToday ? colors.nightDark : colors.night};
  padding: 4px 10px;
  border-radius: 6px;
  background: ${({ isToday }) => isToday
    ? 'rgba(79, 134, 247, 0.08)'
    : 'rgba(0, 0, 0, 0.035)'};
  white-space: nowrap;
  letter-spacing: 0.2px;
`;

const ClosedChip = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.danger};
  font-weight: ${fonts.weight.bold};
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(231, 80, 90, 0.07);
`;

const AllDayChip = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.successDark};
  font-weight: ${fonts.weight.bold};
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(43, 200, 102, 0.08);
`;
